import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useLoader } from '@/hooks/use-loader';
import { useCurrentProject } from '@/lib/current-project';
import { listProjects } from '@/lib/projects-api';
import { buildNavItems, type NavItem, type NavProject } from '@/navigation/nav-items';
import { useInbox } from '@/components/inbox/use-inbox';
import { projectSlugFromPath } from '@/navigation/project-paths';

/**
 * The sidebar's items for this page: every project the caller can see (shared with the Projects
 * page's list, so a new, renamed or archived project shows here too), plus the project in the URL
 * while the list loads or when it isn't in it (an archived project opened by link).
 */
export function useNavItems(): NavItem[] {
  const { pathname } = useLocation();
  const slug = projectSlugFromPath(pathname);
  const current = useCurrentProject();
  const currentName = current && current.slug === slug ? current.name : null;
  const currentColour = current && current.slug === slug ? current.color : undefined;
  const currentIcon = current && current.slug === slug ? current.icon : undefined;
  const loader = useLoader((signal) => listProjects({ signal }), [], { key: 'factory:projects' });
  const listed = loader.data?.data?.projects;
  const inboxCount = useInbox().unread;
  return useMemo(() => {
    const projects: NavProject[] = (listed ?? []).map((p) => ({
      slug: p.slug,
      // The current project's name, colour and icon are the freshest (its settings may have just changed them).
      name: p.slug === slug && currentName ? currentName : p.name,
      color: p.slug === slug && currentColour ? currentColour : p.color,
      icon: p.slug === slug && currentIcon !== undefined ? currentIcon : p.icon,
    }));
    if (slug && !projects.some((p) => p.slug === slug)) projects.unshift({ slug, name: currentName ?? slug, color: currentColour, icon: currentIcon });
    return buildNavItems(projects, inboxCount);
  }, [listed, slug, currentName, currentColour, currentIcon, inboxCount]);
}
